import {
  ChatCircleDots, Quotes, Tag, Warning, ShoppingCart, LinkSimple, User, Package, SealCheck,
} from "@phosphor-icons/react";
import { Tip, platformName, evidenceLabel, buildVerdict } from "./commerceUi";

const Panel = ({ icon: Icon, title, tip, children, testid }) => (
  <div className="rounded-2xl bg-white/80 backdrop-blur border border-violet-100 p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 text-violet-600 font-bold text-sm mb-3">
      <Icon size={16} weight="duotone" /> {title}{tip && <Tip text={tip} />}
    </div>
    {children}
  </div>
);

const MixBar = ({ dist, colors }) => {
  const total = Object.values(dist || {}).reduce((a, b) => a + b, 0) || 1;
  const order = ["high", "medium", "low"];
  return (
    <div className="space-y-2">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-zinc-100">
        {order.filter((k) => dist?.[k]).map((k) => (
          <div key={k} className={colors[k]} style={{ width: `${(dist[k] / total) * 100}%` }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-500">
        {order.filter((k) => dist?.[k]).map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${colors[k]}`} />
            {k} · {dist[k]}
          </span>
        ))}
      </div>
    </div>
  );
};

const VERDICT_TONE = {
  emerald: "from-emerald-50 border-emerald-200 text-emerald-700",
  amber: "from-amber-50 border-amber-200 text-amber-700",
  rose: "from-rose-50 border-rose-200 text-rose-700",
};

const FinalVerdict = ({ insight }) => {
  const v = buildVerdict(insight);
  return (
    <div className={`rounded-2xl bg-gradient-to-br to-white border p-5 shadow-sm ${VERDICT_TONE[v.tone]}`} data-testid="commerce-final-verdict">
      <div className="flex items-center gap-2 text-sm font-bold mb-2">
        <SealCheck size={17} weight="fill" /> Final Verdict
        <Tip text="A plain-English summary of demand, competition, margins and difficulty from the analysis above." />
      </div>
      <div className="text-xl font-extrabold text-zinc-900">{v.label}</div>
      <ul className="mt-3 space-y-1.5">
        {v.bullets.map((b, i) => (
          <li key={i} className="text-sm text-zinc-700 flex gap-2"><span className="text-zinc-400 flex-shrink-0">•</span>{b}</li>
        ))}
      </ul>
      <p className="mt-3 text-sm font-semibold text-zinc-900 border-t border-zinc-200/70 pt-2.5">{v.nextStep}</p>
    </div>
  );
};

const DIM_META = [
  ["customer_voice", "Customer Voice", "What customers are frustrated with"],
  ["search_intent", "Search Intent", "What people actively search for"],
  ["buying_intent", "Buying Intent", "What people already buy (later phase)"],
  ["market_intelligence", "Market Intelligence", "How companies compete (later phase)"],
];

const DimensionScores = ({ scores }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="commerce-dimension-scores">
    {DIM_META.map(([key, label, tip]) => {
      const v = (scores || {})[key];
      return (
        <div key={key} className={`rounded-xl border px-3 py-2.5 ${v != null ? "bg-white border-violet-100" : "bg-zinc-50 border-zinc-100"}`}>
          <div className={`text-lg font-black leading-none ${v != null ? "text-zinc-900" : "text-zinc-300"}`}>{v != null ? Math.round(v) : "—"}</div>
          <div className="text-[10px] text-zinc-500 mt-1 flex items-center">{label}<Tip text={tip} /></div>
        </div>
      );
    })}
  </div>
);

const EvidenceByDimension = ({ dims }) => {
  const entries = Object.entries(dims || {});
  if (entries.length === 0) return null;
  return (
    <Panel icon={ShoppingCart} title="Evidence by Dimension" tip="Where the evidence came from and what type it is." testid="commerce-cluster-sources">
      <div className="space-y-3">
        {entries.map(([dim, plats]) => (
          <div key={dim}>
            <div className="text-[11px] font-bold uppercase tracking-wide text-violet-600 mb-1">{dim}</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(plats).map(([p, n]) => (
                <span key={p} className="text-xs px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-100">
                  {platformName(p)} · {n}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const RealClusterDetail = ({ cluster, insight }) => {
  if (!cluster) return null;
  const m = cluster.metrics || {};

  return (
    <div className="space-y-4" data-testid="commerce-real-cluster-detail">
      {/* Header */}
      <div className="rounded-2xl bg-white/85 backdrop-blur border border-violet-200 p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-600 bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 rounded-full">
              Customer Opportunity
            </span>
            <h2 className="mt-2.5 text-xl sm:text-2xl font-extrabold text-zinc-900 leading-tight">{cluster.label}</h2>
            {cluster.desired_outcome && (
              <p className="mt-1.5 text-sm text-zinc-500">Wants: <span className="text-zinc-700 font-medium">{cluster.desired_outcome}</span></p>
            )}
          </div>
          <div className="text-center flex-shrink-0">
            <div className="text-3xl font-black text-zinc-900 leading-none">{cluster.craving_score}</div>
            <div className="text-[10px] text-zinc-400 mt-1 font-semibold uppercase tracking-wide flex items-center justify-center">
              Opportunity Score<Tip text="How strong and consistent this customer problem is across all customer signals." />
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {[
            ["Customer Signals", cluster.member_count, "complaints grouped", "Number of separate customer complaints or requests grouped into this opportunity."],
            ["Evidence Links", cluster.source_count, "supporting mentions", "Distinct sources backing up this customer problem."],
            ["AI Confidence", m.avg_confidence != null ? `${Math.round(m.avg_confidence * 100)}%` : "—", "genuine problem", "How confident the AI is that this represents a genuine customer problem."],
            ["Platforms", Object.keys(cluster.sources || {}).length, Object.keys(cluster.sources || {}).map(platformName).join(", ") || "—", "Which platforms these customer signals came from."],
          ].map(([label, value, sub, tip]) => (
            <div key={label} className="rounded-xl bg-violet-50/60 border border-violet-100 px-3 py-2.5">
              <div className="text-lg font-black text-zinc-900 leading-none">{value}</div>
              <div className="text-[10px] text-zinc-500 mt-1 flex items-center">{label}<Tip text={tip} /></div>
              <div className="text-[9px] text-zinc-400">{sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-zinc-500">
          {cluster.customer_type && (
            <span className="inline-flex items-center gap-1.5"><User size={13} /> {cluster.customer_type}</span>
          )}
          {cluster.product_category_hint && (
            <span className="inline-flex items-center gap-1.5"><Package size={13} /> {cluster.product_category_hint}</span>
          )}
        </div>
      </div>

      {/* Four independent evidence dimensions */}
      <Panel icon={SealCheck} title="Evidence Dimensions" tip="Four independent lenses. Overall Opportunity Score blends them." testid="commerce-cluster-dimensions">
        <DimensionScores scores={cluster.dimension_scores} />
      </Panel>

      {/* Customer complaints */}
      {cluster.sample_pains?.length > 0 && (
        <Panel icon={ChatCircleDots} title="Customer Complaints" tip="Real problems customers described, grouped into this opportunity." testid="commerce-cluster-pains">
          <ul className="space-y-2">
            {cluster.sample_pains.map((p, i) => (
              <li key={i} className="text-sm text-zinc-700 flex gap-2">
                <span className="text-violet-400 font-bold flex-shrink-0">•</span>{p}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Example quotes */}
      {cluster.sample_quotes?.length > 0 && (
        <Panel icon={Quotes} title="Example Quotes" tip="Actual customer wording pulled from the source discussions." testid="commerce-cluster-quotes">
          <div className="space-y-2.5">
            {cluster.sample_quotes.map((q, i) => (
              <div key={i} className="rounded-xl bg-zinc-50 border border-zinc-100 px-3.5 py-2.5">
                <p className="text-sm text-zinc-700 italic">“{q.text}”</p>
                <div className="mt-1.5 flex items-center gap-2 text-[10px] text-zinc-400">
                  <span className="uppercase font-bold tracking-wide">{platformName(q.source)}</span>
                  {q.url && (
                    <a href={q.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-violet-500 hover:underline">
                      <LinkSimple size={11} /> {evidenceLabel(q.url)}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* Keywords + platforms */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Panel icon={Tag} title="People Search For" tip="Search phrases associated with this problem." testid="commerce-cluster-keywords">
          <div className="flex flex-wrap gap-2">
            {(cluster.top_keywords || []).map((k) => (
              <span key={k} className="text-xs font-medium px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-100">{k}</span>
            ))}
            {(!cluster.top_keywords || cluster.top_keywords.length === 0) && <span className="text-xs text-zinc-400">No keywords</span>}
          </div>
        </Panel>
        <EvidenceByDimension dims={cluster.evidence_dimensions} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Panel icon={Warning} title="Problem Severity" tip="How serious this problem appears across customer discussions." testid="commerce-cluster-severity">
          <MixBar dist={cluster.severity_dist} colors={{ high: "bg-rose-500", medium: "bg-amber-400", low: "bg-emerald-400" }} />
        </Panel>
        <Panel icon={ShoppingCart} title="Purchase Intent" tip="How likely customers are to spend money to solve this problem." testid="commerce-cluster-intent">
          <MixBar dist={cluster.buying_intent_dist} colors={{ high: "bg-emerald-500", medium: "bg-amber-400", low: "bg-zinc-400" }} />
        </Panel>
      </div>

      {/* Evidence */}
      {cluster.source_urls?.length > 0 && (
        <Panel icon={LinkSimple} title={`Evidence (${cluster.source_urls.length})`} tip="Open the original discussions and searches behind this opportunity." testid="commerce-cluster-links">
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {cluster.source_urls.map((u, i) => (
              <a key={i} href={u} target="_blank" rel="noreferrer"
                className="block text-xs text-violet-600 hover:underline">{evidenceLabel(u)}</a>
            ))}
          </div>
        </Panel>
      )}

      {/* Final Verdict (from stored pipeline outputs) */}
      <FinalVerdict insight={{ ...(insight || {}), buying_intent_dist: cluster.buying_intent_dist }} />
    </div>
  );
};

export default RealClusterDetail;
